import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/Textarea';
import { toastSuccess, toastError } from '@/components/ui/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { API_BASE_URL } from '@/config';

export default function TwoFAResetRequest() {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    reason: '',
    last_login_date: '',
    account_creation_date: '',
    recent_activity_description: '',
    security_question_1: t('twoFAResetRequest.securityQuestions.question1'),
    security_answer_1: '',
    security_question_2: t('twoFAResetRequest.securityQuestions.question2'),
    security_answer_2: '',
    security_question_3: t('twoFAResetRequest.securityQuestions.question3'),
    security_answer_3: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [requestId, setRequestId] = useState('');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.email || !formData.full_name || !formData.reason) {
      toastError({
        title: t('common.error'),
        description: t('twoFAResetRequest.errors.fillRequiredFields'),
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/2fa/reset-request`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('twoFAResetRequest.errors.submitFailed'));
      }

      setRequestId(data.request_id);
      setIsSubmitted(true);
      toastSuccess({
        title: t('twoFAResetRequest.success.requestSubmitted'),
        description: data.message,
      });

    } catch (error) {
      console.error('2FA reset request failed:', error);
      toastError({
        title: t('twoFAResetRequest.errors.requestFailed'),
        description: error instanceof Error ? error.message : t('twoFAResetRequest.errors.submitFailed'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[500px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{t('twoFAResetRequest.success.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('twoFAResetRequest.success.description')}
          </p>
        </div>

        <div className="grid gap-6">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-900/20 dark:border-green-800">
            <h3 className="font-medium text-green-900 dark:text-green-100 mb-2">{t('twoFAResetRequest.success.submittedSuccessfully')}</h3>
            <div className="text-sm text-green-800 dark:text-green-200 space-y-2">
              <p><strong>{t('twoFAResetRequest.success.email')}:</strong> {formData.email}</p>
              <p><strong>{t('twoFAResetRequest.success.requestId')}:</strong> {requestId}</p>
              <p><strong>{t('twoFAResetRequest.success.status')}:</strong> {t('twoFAResetRequest.success.pendingReview')}</p>
            </div>
          </div>

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
            <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-2">{t('twoFAResetRequest.success.whatHappensNext')}</h3>
            <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <p>{t('twoFAResetRequest.success.step1')}</p>
              <p>{t('twoFAResetRequest.success.step2')}</p>
              <p>{t('twoFAResetRequest.success.step3')}</p>
              <p>{t('twoFAResetRequest.success.step4')}</p>
            </div>
          </div>
          
          <div className="flex flex-col space-y-2">
            <Button asChild className="w-full">
              <Link to="/login">{t('twoFAResetRequest.success.backToLogin')}</Link>
            </Button>

            <Button
              variant="outline"
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  email: '',
                  full_name: '',
                  reason: '',
                  last_login_date: '',
                  account_creation_date: '',
                  recent_activity_description: '',
                  security_question_1: t('twoFAResetRequest.securityQuestions.question1'),
                  security_answer_1: '',
                  security_question_2: t('twoFAResetRequest.securityQuestions.question2'),
                  security_answer_2: '',
                  security_question_3: t('twoFAResetRequest.securityQuestions.question3'),
                  security_answer_3: ''
                });
                setRequestId('');
              }}
              className="w-full"
            >
              {t('twoFAResetRequest.success.submitAnother')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[500px]">
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{t('twoFAResetRequest.form.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('twoFAResetRequest.form.description')}
        </p>
      </div>

      <div className="grid gap-6">
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg dark:bg-yellow-900/20 dark:border-yellow-800">
          <h3 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">{t('twoFAResetRequest.form.securityNotice')}</h3>
          <div className="text-sm text-yellow-800 dark:text-yellow-200 space-y-1">
            <p>{t('twoFAResetRequest.form.notice1')}</p>
            <p>{t('twoFAResetRequest.form.notice2')}</p>
            <p>{t('twoFAResetRequest.form.notice3')}</p>
            <p>{t('twoFAResetRequest.form.notice4')}</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Required Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t('twoFAResetRequest.form.requiredInformation')}</h3>

            <div className="grid gap-2">
              <Label htmlFor="email">{t('twoFAResetRequest.form.emailAddress')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('twoFAResetRequest.form.placeholders.email')}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={isLoading}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="full_name">{t('twoFAResetRequest.form.fullName')}</Label>
              <Input
                id="full_name"
                type="text"
                placeholder={t('twoFAResetRequest.form.placeholders.fullName')}
                value={formData.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                disabled={isLoading}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="reason">{t('twoFAResetRequest.form.reason')}</Label>
              <Textarea
                id="reason"
                placeholder={t('twoFAResetRequest.form.placeholders.reason')}
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                disabled={isLoading}
                rows={3}
                required
              />
            </div>
          </div>
          
          {/* Optional Verification Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t('twoFAResetRequest.form.additionalVerification')}</h3>

            <div className="grid gap-2">
              <Label htmlFor="last_login_date">{t('twoFAResetRequest.form.lastLoginDate')}</Label>
              <Input
                id="last_login_date"
                type="text"
                placeholder={t('twoFAResetRequest.form.placeholders.lastLoginDate')}
                value={formData.last_login_date}
                onChange={(e) => handleInputChange('last_login_date', e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="account_creation_date">{t('twoFAResetRequest.form.accountCreationDate')}</Label>
              <Input
                id="account_creation_date"
                type="text"
                placeholder={t('twoFAResetRequest.form.placeholders.accountCreationDate')}
                value={formData.account_creation_date}
                onChange={(e) => handleInputChange('account_creation_date', e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="recent_activity_description">{t('twoFAResetRequest.form.recentActivity')}</Label>
              <Textarea
                id="recent_activity_description"
                placeholder={t('twoFAResetRequest.form.placeholders.recentActivity')}
                value={formData.recent_activity_description}
                onChange={(e) => handleInputChange('recent_activity_description', e.target.value)}
                disabled={isLoading}
                rows={2}
              />
            </div>
          </div>
          
          {/* Security Questions */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">{t('twoFAResetRequest.form.securityQuestionsTitle')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('twoFAResetRequest.form.securityQuestionsDescription')}
            </p>

            <div className="grid gap-2">
              <Label htmlFor="security_answer_1">{formData.security_question_1}</Label>
              <Input
                id="security_answer_1"
                type="text"
                placeholder={t('twoFAResetRequest.form.placeholders.yourAnswer')}
                value={formData.security_answer_1}
                onChange={(e) => handleInputChange('security_answer_1', e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="security_answer_2">{formData.security_question_2}</Label>
              <Input
                id="security_answer_2"
                type="text"
                placeholder={t('twoFAResetRequest.form.placeholders.yourAnswer')}
                value={formData.security_answer_2}
                onChange={(e) => handleInputChange('security_answer_2', e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="security_answer_3">{formData.security_question_3}</Label>
              <Input
                id="security_answer_3"
                type="text"
                placeholder={t('twoFAResetRequest.form.placeholders.yourAnswer')}
                value={formData.security_answer_3}
                onChange={(e) => handleInputChange('security_answer_3', e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          <Button disabled={isLoading} className="w-full">
            {isLoading ? t('twoFAResetRequest.form.submittingRequest') : t('twoFAResetRequest.form.submitButton')}
          </Button>
        </form>
        
        <div className="flex flex-col space-y-2 text-center">
          <Button asChild variant="ghost" className="w-full">
            <Link to="/login">{t('twoFAResetRequest.form.backToLogin')}</Link>
          </Button>

          <p className="text-sm text-muted-foreground">
            {t('twoFAResetRequest.form.rememberDevice')}{' '}
            <Link to="/verify-2fa" className="underline underline-offset-4 hover:text-primary">
              {t('twoFAResetRequest.form.tryVerification')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
